/*!SECTION
	@info : 환경 종합데이터
	@developed by : Leo(WiseLake)
	@date : 2024-03-05
*/
import { useState, useEffect, useCallback, useMemo } from 'react';
import styled from "styled-components/macro";
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

//! 공통 사용을 위한 전역 XX 생성
dayjs.locale('ko');
dayjs.extend(utc);

interface ExcelDataCrossConProps {
	setTagList: React.Dispatch<React.SetStateAction<any[]>>;
}


const ExcelDataCrossCon: React.FC<ExcelDataCrossConProps> = ({ setTagList }) => {
	/** 사전 필요 데이터 정의
	 * * 농장 데이터를 기반으로 하위 정보들을 불러옴
	 * *. *. 사육실 정보
	 * *. *. 사육 구역 정보
	 * *. *. *. 위 선택(selectTagList)에 따른 환경 종합데이터 출력
	*/

	//! 데이터를 관리하기 위한 state 선언

	// 농장 정보를 가진 localStorage를 가져옴
	const targetFarmInfo = JSON.parse(localStorage.getItem("targetfarmInfo") || "{}");
	const targetFarmInfoMemo = useMemo(() => targetFarmInfo, [JSON.stringify(targetFarmInfo)]);

	// 사육실 Combobox List 정보를 담는 state
	const [placeList, setPlaceList] = useState<any[]>([]);

	// 사육구역 Combobox List 정보를 담는 state
	const [placePartList, setPlacePartList] = useState<any[]>([]);

	// 환경 정보를 담는 state
	const [envirDataList, setEnvirDataList] = useState<any[]>([]);

	// 선택된 option을 담는 state
	const [selectFarm, setSelectFarm] = useState<number>(targetFarmInfoMemo.placeId);
	const [selectPlace, setSelectPlace] = useState<number>(0);
	const [selectPlacePart, setSelectPlacePart] = useState<number>(0);

	// 선택된 Tag의 tagId(number) List를 담는 state
	const [selectTagList, setSelectTagList] = useState<any[]>([]);


	//! API
	// 농장 정보를 기반으로 사육실 정보, 사육구역을 가져오는 API	
	const getPlaceInfo = async (placeId: number) => {
		try {
			const response = await axios.get(`/ifactory/api/place/specificplace?placeId=${placeId}`);
			return response.data;
		} catch (error: any) {
			console.error(error);
		}
	};

	// 사육구역을 기반으로 환경 정보를 가져오는 API
	const getTag = async (placeId: number) => {
		try {
			const response = await axios.get(`/ifactory/api/tag/${placeId}`);
			return response.data;
		} catch (error: any) {
			console.error(error);
		}
	};

	//! React Hook
	// mount 시 실행되는 함수
	useEffect(() => {
		// 농장 정보를 기반으로 사육실 정보를 가져옴
		const placeId = targetFarmInfoMemo.placeId;
		getPlaceInfo(placeId).then((response) => {
			setPlaceList(response);
		});
	}, []);

	//! fn
	// 농장을 선택했을 때 실행되는 함수
	const handleFarmSelect = (farmId: number) => {
		return () => {
			setSelectFarm(farmId);
			// 선택된 사육실, 사육구역 정보를 초기화
			setSelectPlace(0);
			setSelectPlacePart(0);
			// 사육구역 List 초기화
			setPlacePartList([]);

			getPlaceInfo(farmId).then((response) => {
				setPlaceList(response);
			});

			getTag(farmId).then((response) => {
				setEnvirDataList(response);
			});
		};
	};

	// 사육실을 선택했을 때 실행되는 함수
	const handlePlaceSelect = (placeId: number) => {
		return () => {
			setSelectPlace(placeId);
			// 선택된 사육구역 정보를 초기화
			setSelectPlacePart(0);

			getPlaceInfo(placeId).then((response) => {
				setPlacePartList(response);
			});

			getTag(placeId).then((response) => {
				setEnvirDataList(response);
			});
		};
	};

	// 사육구역을 선택했을 때 실행되는 함수
	const handlePlacePartSelect = (placeId: number) => {
		return () => {
			setSelectPlacePart(placeId);

			getTag(placeId).then((response) => {
				setEnvirDataList(response);
			});
		};
	};

	const handleTagSelect = (tag: any) => {
		return () => {
			// 선택된 tagId가 selectTagList에 없으면 추가, 있으면 삭제
			if (selectTagList.some((item) => item.tagId === tag.tagId)) {
				setSelectTagList(selectTagList.filter((item) => item.tagId !== tag.tagId));
			} else {
				setSelectTagList([...selectTagList, tag]);
			}
		};
	};

	useEffect(() => {
		setTagList(selectTagList);
	}, [selectTagList]);


	//! render
	return (

		/**
		 * 1. 불러온 placeList를 기반으로 사육실 목록을 토글할 수 있는 버튼 구성.
		 * 2. 이후에 사육실을 클릭하면 해당 사육실 기반으로 사육구역을 토글할 수 있는 버튼 구성
		 * 3. 위 1,2번을 통해 선택된 사육구역에 따른 환경 정보를 토글할 수 있는 버튼 구성
		 * 4. 위 3번을 통해 선택된 환경 정보를 나타내주는 탭 구성
		 *  */
		<ExcelDataCrossConStyle>
			<Title>환경 종합데이터 검색옵션</Title>

			<SelectOptionDivStyle>
				{/* 농장 구성 */}
				<SelectOptionBoxStyle>
					<p>농장</p>
					<ul>
						<li>
							<button
								className={selectFarm === targetFarmInfoMemo.placeId ? 'active' : ''}
								onClick={handleFarmSelect(targetFarmInfoMemo.placeId)}
							>{targetFarmInfoMemo.name}</button>
						</li>
					</ul>
				</SelectOptionBoxStyle>

				{/* 사육실 구성 */}
				<SelectOptionBoxStyle>
					<p>사육실</p>
					<ul>
						{placeList.map((place, idx) => {
							return (
								<li key={idx}>
									<button
										className={selectPlace === place.placeId ? 'active' : ''}
										onClick={handlePlaceSelect(Number(place.placeId))}>
										{place.name}
									</button>
								</li>
							);
						})}
					</ul>
				</SelectOptionBoxStyle>

				{/* 사육구역 구성 */}
				<SelectOptionBoxStyle>
					<p>사육구역</p>
					<ul>
						{placePartList.map((place, idx) => {
							return (
								<li key={idx}>
									<button
										className={selectPlacePart === place.placeId ? 'active' : ''}
										onClick={handlePlacePartSelect(Number(place.placeId))}>
										{place.name}
									</button>
								</li>
							);
						})}
					</ul>
				</SelectOptionBoxStyle>

				{/* 환경 정보 구성 */}
				<TagListStyle>
					<ul>
						{envirDataList.map((env, idx) => {
							return (
								<li key={idx}>
									<button type="button"
										onClick={handleTagSelect(env)}
										className={selectTagList.some(tag => tag.tagId === env.tagId) ? 'active' : ''}>
										{env.name}
									</button>
								</li>
							);
						})}
					</ul>
				</TagListStyle>
			</SelectOptionDivStyle>

			<SelectedTagListStyle>
				{/* 선택된 환경 정보에 따른 그래프 */}
				<ul>
					{
						selectTagList.length === 0 ? <li className='noTag'>선택된 태그 정보가 없습니다.</li> :
							selectTagList.map((tag, idx) => {
								return (
									<li key={idx}>
										<span>{tag.name}</span>
										<button type="button" className='close-tab'
											onClick={() => setSelectTagList(selectTagList.filter((item) => item.tagId !== tag.tagId))}>
											✖
										</button>
									</li>
								);
							})


					}
				</ul>
			</SelectedTagListStyle>

		</ExcelDataCrossConStyle>
	);
};

export default ExcelDataCrossCon;

const ExcelDataCrossConStyle = styled.div`
	// 태그의 기본 설정 reset
	p, ul, li, button{
		margin: 0;
		padding: 0;
		border: none;
		list-style: none;
		text-decoration: none;
		font-size: 1em;
		font-weight: normal;
		color: #333;
		background-color: transparent;
	}

	padding: 20px 40px 0 40px;
	margin-bottom: 10px;
`;

const Title = styled.div`
	font-size: 23px;
  color: #2a3a4b;
  margin-bottom: 20px;
`;

const SelectOptionDivStyle = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex-direction: column;

	border : 1px solid #ddd;

	>div{
		&:not(:last-child){
			border-bottom: 1px solid #ddd;
		};
	}
	
	
`;

const SelectOptionBoxStyle = styled.div`
	width: 100%;
	display: flex;
	align-items: stretch;

	p{
		min-height: 40px;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 0.9em;
		font-weight: bold;
		background-color: #eee;
		padding: 0 10px;
		min-width: 100px;
	}
	ul{
		display: flex;
		justify-content: flex-start;
		flex-wrap: wrap;
		width: 100%;
		padding: 5px;

		li{
			padding: 5px;
			
			button{
				font-size : 0.9em;
				padding: 8px;
				border-radius: 8px;
				background-color: #eee;
				&:hover{
					cursor: pointer;
					background-color: #73972c;
					color: white;
				}
				&.active{
					background-color: #73972c;
					color: white;
				}
			}
		}
	}
`;

const TagListStyle = styled.div`
	width: 100%;
	display: flex;
	align-items: center;
	min-height: 40px;

	ul{
		display: flex;
		flex-wrap: wrap;
		padding: 10px;
		li{
			&:not(:last-child){
				margin-right: 10px;
			}
			button{
				font-size : 0.75em;
				padding: 8px;
				border-radius: 8px;
				background-color: #fff;
				border: 1px solid #d9d9d9;
				&:hover{
					cursor: pointer;
					color: #73972c;
					font-weight: bold;
				}
				&.active{
					color: #73972c;
					font-weight: bold;
				}
				&::before{
					content: '# ';
				}
			}
		}
	}
`;


const SelectedTagListStyle = styled.div`
	width: 100%;
	display: flex;
	align-items: center;
	margin-top: 10px;
	border-top: 4px solid #ddd;
	border-bottom: 1px solid #ddd;
	background-color: #c6c6c630;

	ul{
		display: flex;
		flex-wrap: wrap;
		padding: 8px 10px 0px 10px;
		li{
			padding: 4px 8px;
			border-radius: 8px;
			background-color: #73972c;
			color: white;

			display: flex;
			align-items: center;

			margin-bottom: 8px;

			&:not(:last-child){
				margin-right: 10px;
			}
			span{
				font-size : 0.75em;
				
				&::after{
					content: '｜';
					margin: 0 5px;
				}
			}
			button{
				font-size : 0.75em;
				color: white;
				padding: 2px;
				&:hover{
					cursor: pointer;
					color: #fff;
					transform: rotate(90deg);
					transition: all 0.3s ease;
				}
			}

			&.noTag{
				background-color: #888888;
				color: #fff;
			}
		}
	}
`;
